import React, { useEffect, useState } from "react";
import { SaveIcon, CancelIcon } from "./icons";

// Preferences-only editor for a client.
// "Show Details" on the client card opens the full demographic view
// (ProfileDetailsModal) — this modal is intentionally narrower in
// scope: it only edits what kind of profiles the bureau should send
// this client. Splitting the two makes the bureau's intent explicit
// when they open it.
// Height in the DB is one Number per side (total inches), but humans
// pick height in feet+inches. Convert between the two on the modal
// boundary so the form stays human and the backend payload stays clean.
const splitInches = (total) => {
  if (typeof total !== "number" || Number.isNaN(total)) {
    return { ft: "", inch: "" };
  }
  return { ft: String(Math.floor(total / 12)), inch: String(total % 12) };
};

const toTotalInches = ({ ft, inch }) => {
  const f = ft === "" ? null : Number(ft);
  const i = inch === "" ? 0 : Number(inch);
  if (f === null || Number.isNaN(f)) return undefined;
  if (Number.isNaN(i)) return undefined;
  return f * 12 + i;
};

const ClientDetailsModal = ({ isOpen, client, onClose, onUpdateClient }) => {
  const [form, setForm] = useState({
    clientNotes: "",
    preferredGender: "any",
    // Structured min/max replaces the old free-text "25-35" input.
    minAge: "",
    maxAge: "",
    // Height kept as separate feet + inches per side; converted to
    // total inches on submit so it matches the schema.
    minHeightFt: "",
    minHeightIn: "",
    maxHeightFt: "",
    maxHeightIn: "",
    preferredCountries: [],
    preferredCities: [],
    preferredProfessions: [],
    preferredCastes: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (isOpen && client) {
      const minH = splitInches(client.preferredHeightMin);
      const maxH = splitInches(client.preferredHeightMax);
      // The matrimonial setting makes the preferred gender deterministic
      // once we know the client's own gender — they're looking for the
      // opposite. Pre-fill that so any stale "any" or same-sex value
      // already on file gets corrected on next save.
      const ownGender = (
        client.gender ||
        client.user?.gender ||
        ""
      ).toLowerCase();
      const oppositeGender =
        ownGender === "male"
          ? "female"
          : ownGender === "female"
          ? "male"
          : null;
      setForm({
        clientNotes: client.clientNotes || "",
        preferredGender:
          oppositeGender || client.preferredGender || "any",
        minAge: client.preferredAgeRange?.min ?? "",
        maxAge: client.preferredAgeRange?.max ?? "",
        minHeightFt: minH.ft,
        minHeightIn: minH.inch,
        maxHeightFt: maxH.ft,
        maxHeightIn: maxH.inch,
        preferredCountries: client.preferredCountries || [],
        preferredCities: client.preferredCities || [],
        preferredProfessions: client.preferredProfessions || [],
        preferredCastes: client.preferredCastes || [],
      });
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, client]);

  if (!isOpen || !client) return null;

  // Recomputed on every render so the locked field stays in sync if
  // the parent ever swaps the client doc out without re-mounting.
  const ownGender = (client.gender || client.user?.gender || "").toLowerCase();
  const oppositeGender =
    ownGender === "male"
      ? "female"
      : ownGender === "female"
      ? "male"
      : null;
  const genderLocked = Boolean(oppositeGender);

  const handleArrayChange = (field, value) => {
    const array = value.split(",").map((item) => item.trim()).filter(Boolean);
    setForm((prev) => ({ ...prev, [field]: array }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Bail early on inverted ranges — the schema would accept these
    // silently and produce weird match results.
    const minA = form.minAge === "" ? null : Number(form.minAge);
    const maxA = form.maxAge === "" ? null : Number(form.maxAge);
    if (minA !== null && maxA !== null && minA > maxA) {
      setError("Minimum age can't be greater than maximum age.");
      return;
    }
    const minHTotal = toTotalInches({
      ft: form.minHeightFt,
      inch: form.minHeightIn,
    });
    const maxHTotal = toTotalInches({
      ft: form.maxHeightFt,
      inch: form.maxHeightIn,
    });
    if (
      minHTotal !== undefined &&
      maxHTotal !== undefined &&
      minHTotal > maxHTotal
    ) {
      setError("Minimum height can't be greater than maximum height.");
      return;
    }

    // Enforce the opposite-gender rule on submit too. The field is
    // locked in the UI when the client's gender is known, but this
    // guard catches anything that somehow slipped through (stale form
    // state, future caller, etc.).
    if (oppositeGender && form.preferredGender !== oppositeGender) {
      setError(
        `Preferred gender must be ${oppositeGender} (opposite of client's ${ownGender}).`
      );
      return;
    }

    try {
      setIsSaving(true);
      const ageRange = {};
      if (minA !== null && !Number.isNaN(minA)) ageRange.min = minA;
      if (maxA !== null && !Number.isNaN(maxA)) ageRange.max = maxA;

      const payload = {
        clientNotes: form.clientNotes,
        preferredGender: oppositeGender || form.preferredGender,
        preferredAgeRange: Object.keys(ageRange).length ? ageRange : undefined,
        preferredHeightMin: minHTotal,
        preferredHeightMax: maxHTotal,
        preferredCountries: form.preferredCountries,
        preferredCities: form.preferredCities,
        preferredProfessions: form.preferredProfessions,
        preferredCastes: form.preferredCastes,
      };

      await onUpdateClient(client._id, payload);
      setSuccess("Preferences updated successfully.");
    } catch (err) {
      setError(err.message || "Failed to update preferences");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden bg-[var(--color-surface)] rounded-xl shadow-xl">
        {/* HEADER — always visible */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--color-border)] flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-[var(--color-text-strong)]">
              Client Preferences
            </h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              {client.user?.fullName
                ? `What kind of profiles ${client.user.fullName} is open to.`
                : "What kind of profiles this client is open to."}
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* BODY — scrolls */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">Client Notes</label>
                <textarea
                  value={form.clientNotes}
                  onChange={(e) => setForm((prev) => ({ ...prev, clientNotes: e.target.value }))}
                  rows={3}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  placeholder="Any notes about this client's preferences"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                  Preferred Gender
                </label>
                <select
                  value={form.preferredGender}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferredGender: e.target.value,
                    }))
                  }
                  disabled={genderLocked}
                  className={`w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${
                    genderLocked ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {/* When the client's own gender is known, only the
                      opposite value is even an option — matrimonial
                      matching always pairs across the gender, so "any"
                      or same-gender would always produce wrong matches.
                      When gender is unknown (legacy/imported profiles
                      with no recorded gender) we fall back to the full
                      list so the bureau can pick. */}
                  {genderLocked ? (
                    <option value={oppositeGender}>
                      {oppositeGender === "male" ? "Male" : "Female"}
                    </option>
                  ) : (
                    <>
                      <option value="any">Any</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </>
                  )}
                </select>
                {genderLocked && (
                  <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                    Auto-set to the opposite of the client's gender
                    ({ownGender}). Update the client's demographic
                    details if this is wrong.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                  Preferred Age Range
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    min={18}
                    max={80}
                    inputMode="numeric"
                    value={form.minAge}
                    onChange={(e) => {
                      // Cap at 80 while typing; allow blank; snap to 18
                      // on blur. Negatives blocked at keypress.
                      const raw = e.target.value;
                      if (raw === "") {
                        setForm((prev) => ({ ...prev, minAge: "" }));
                        return;
                      }
                      const num = Number(raw);
                      if (Number.isNaN(num)) return;
                      const clamped = num > 80 ? 80 : num < 0 ? 0 : num;
                      setForm((prev) => ({ ...prev, minAge: String(clamped) }));
                    }}
                    onBlur={(e) => {
                      const num = Number(e.target.value);
                      if (e.target.value === "" || Number.isNaN(num)) return;
                      if (num < 18) setForm((prev) => ({ ...prev, minAge: "18" }));
                      else if (num > 80) setForm((prev) => ({ ...prev, minAge: "80" }));
                    }}
                    onKeyDown={(e) => {
                      if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
                    }}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    placeholder="Min age"
                  />
                  <input
                    type="number"
                    min={18}
                    max={80}
                    inputMode="numeric"
                    value={form.maxAge}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        setForm((prev) => ({ ...prev, maxAge: "" }));
                        return;
                      }
                      const num = Number(raw);
                      if (Number.isNaN(num)) return;
                      const clamped = num > 80 ? 80 : num < 0 ? 0 : num;
                      setForm((prev) => ({ ...prev, maxAge: String(clamped) }));
                    }}
                    onBlur={(e) => {
                      const num = Number(e.target.value);
                      if (e.target.value === "" || Number.isNaN(num)) return;
                      if (num < 18) setForm((prev) => ({ ...prev, maxAge: "18" }));
                      else if (num > 80) setForm((prev) => ({ ...prev, maxAge: "80" }));
                    }}
                    onKeyDown={(e) => {
                      if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
                    }}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    placeholder="Max age"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                  Preferred Height
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
                      Min
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={9}
                          value={form.minHeightFt}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              minHeightFt: e.target.value,
                            }))
                          }
                          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 pr-9 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                          placeholder="ft"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--color-text-muted)]">
                          ft
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={11}
                          value={form.minHeightIn}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              minHeightIn: e.target.value,
                            }))
                          }
                          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 pr-9 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                          placeholder="in"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--color-text-muted)]">
                          in
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
                      Max
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={9}
                          value={form.maxHeightFt}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              maxHeightFt: e.target.value,
                            }))
                          }
                          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 pr-9 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                          placeholder="ft"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--color-text-muted)]">
                          ft
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={11}
                          value={form.maxHeightIn}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              maxHeightIn: e.target.value,
                            }))
                          }
                          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 pr-9 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                          placeholder="in"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--color-text-muted)]">
                          in
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">Preferred Countries</label>
                  <input
                    value={form.preferredCountries.join(", ")}
                    onChange={(e) => handleArrayChange("preferredCountries", e.target.value)}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    placeholder="Pakistan, UK, USA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">Preferred Cities</label>
                  <input
                    value={form.preferredCities.join(", ")}
                    onChange={(e) => handleArrayChange("preferredCities", e.target.value)}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    placeholder="Lahore, Karachi, Islamabad"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">Preferred Professions</label>
                  <input
                    value={form.preferredProfessions.join(", ")}
                    onChange={(e) => handleArrayChange("preferredProfessions", e.target.value)}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    placeholder="Doctor, Engineer, Teacher"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">Preferred Castes</label>
                <input
                  value={form.preferredCastes.join(", ")}
                  onChange={(e) => handleArrayChange("preferredCastes", e.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  placeholder="Rajput, Syed, Mughal"
                />
              </div>

              {error && <div className="text-sm text-red-500">{error}</div>}
              {success && <div className="text-sm text-emerald-700">{success}</div>}
            </div>
          </div>
          </div>

          {/* FOOTER — always visible */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
            >
              <CancelIcon className="w-4 h-4" />
              Close
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-50 px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
            >
              <SaveIcon className="w-4 h-4" />
              {isSaving ? "Saving..." : "Save Preferences"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientDetailsModal;
